import { updateExpense } from "@/api/expenses/edit-expense";
import { queryClient } from "@/lib/reactQuery";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { useSelectedState } from "@/components/expenses/expenseStates";

const editExpenseSchema = z.object({
  expenseDescription: z.string({ message: "Enter expense description" }),
  expenseAmount: z.number({ message: "Enter amount" }).nonnegative(),
  paidTo: z.string({ message: "Enter vendor" }),
  paymentMethod: z.string({ message: "Enter payment method" }),
  expenseDate: z.coerce.date(),
});

type CreateExpenseType = z.infer<typeof editExpenseSchema>;

export const editExpense = () => {
  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<CreateExpenseType>({
    resolver: zodResolver(editExpenseSchema),
  });
  const { selectedExpense } = useSelectedState();

  const { mutateAsync: updateExpenseFn } = useMutation({
    mutationKey: ["updateExpense"],
    mutationFn: updateExpense,
    onSuccess: () => {
      toast.success("Expense updated successfully");
      queryClient.invalidateQueries({ queryKey: ["expenses"] }); // Refetch the expenses to update the table
    },
    onError: () => {
      toast.error("Error updating expense");
    },
  });

  useEffect(() => {
    if (selectedExpense) {
      // Reset the form with the new selected expense values
      reset({
        expenseDescription: selectedExpense.expenseDescription || "",
        expenseAmount: selectedExpense.expenseAmount,
        paidTo: selectedExpense.paidTo || "",
        expenseDate: selectedExpense.expenseDate,
        paymentMethod: selectedExpense.paymentMethod || "",
      });
    } else {
      // Reset the form fields to empty values for a new expense
      reset({
        expenseDescription: "",
        expenseAmount: undefined,
        paidTo: "",
        expenseDate: undefined,
        paymentMethod: "",
      });
    }
  }, [selectedExpense, reset]);
  return {
    control,
    register,
    handleSubmit,
    reset,
    updateExpenseFn,
    errors,
    isSubmitting,
  };
};
